import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Intercepteur : ajoute le token JWT à chaque requête
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Intercepteur : gère les erreurs 401 (token expiré)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Redirect vers login seulement si on n'est pas déjà sur /login
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ── Auth API ────────────────────────────────────────────────

export const authAPI = {
  register: (email, password) =>
    api.post('/auth/register', { email, password }),
  login: (email, password) =>
    api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
};

// ── Runs API ────────────────────────────────────────────────

export const runsAPI = {
  list: (limit = 20, offset = 0) =>
    api.get('/runs', { params: { limit, offset } }),
  get: (runId) => api.get(`/runs/${runId}`),
  create: (data) => api.post('/runs', data),
  cancel: (runId) => api.post(`/runs/${runId}/cancel`),
  retryStep: (runId, stepNumber) => api.post(`/runs/${runId}/retry-step`, { step_number: stepNumber }),
  previewPlan: (data) => api.post('/runs/preview-plan', data),
  validatePlan: (runId, approved, feedback) =>
    api.post(`/runs/${runId}/validate-plan`, { approved, feedback }),
  validateStep: (runId, approved, feedback) =>
    api.post(`/runs/${runId}/validate-step`, { approved, feedback }),
  getConversations: (runId) => api.get(`/runs/${runId}/agent-conversations`),
  stream: (runId) => api.get(`/runs/${runId}/stream`),
  export: (runId) => api.post(`/runs/${runId}/export`),
  getContext: (runId) => api.get(`/runs/${runId}/execution-context`),
};

// ── Projects API ────────────────────────────────────────────

export const projectsAPI = {
  list: () => api.get('/projects'),
  get: (projectId) => api.get(`/projects/${projectId}`),
  delete: (projectId) => api.delete(`/projects/${projectId}`),
  preview: (projectId) => api.get(`/projects/${projectId}/preview`),
  files: (projectId, path = '') =>
    api.get(`/projects/${projectId}/files`, { params: { path } }),
  fileContent: (projectId, path) =>
    api.get(`/projects/${projectId}/file-content`, { params: { path } }),
  gitLog: (projectId) => api.get(`/projects/${projectId}/git-log`),
  deploy: (projectId) => api.post(`/projects/${projectId}/deploy`),
  stop: (projectId) => api.post(`/projects/${projectId}/stop`),
};

// ── Preview API ────────────────────────────────────────────

export const previewAPI = {
  start: (projectId) => api.post(`/projects/${projectId}/preview/start`),
  stop: (projectId) => api.post(`/projects/${projectId}/preview/stop`),
  status: (projectId) => api.get(`/projects/${projectId}/preview/status`),
};

// ── Templates API ──────────────────────────────────────────

export const templatesAPI = {
  list: (stack) =>
    api.get('/templates', { params: stack ? { stack } : {} }),
};

// ── Admin API ───────────────────────────────────────────────

export const adminAPI = {
  getUsers: (skip = 0, limit = 50) =>
    api.get('/admin/users', { params: { skip, limit } }),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  testKey: (keyName) => api.post(`/admin/settings/test-key?key_name=${keyName}`),
  getStats: () => api.get('/admin/stats'),
};

export default api;
